import type {
  ComboBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import { Alert, ComboBox } from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { useInventoryConfigsGet } from "@dealer-center/ddc-pages-inventory-config-fetch";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type { ExtendedComboBoxOption } from "./helper";
import { generateOptions } from "./helper";
import { useGetAccountId } from "@dealer-center/ddc-api-account-fetch";

const StyledAlert = styled(Alert)`
  margin-bottom: 1rem;
`;

interface PagesInventoryConfigUiResultsProps {
  id: string;
  setSelectedConfigName: (value: string) => void;
  dealerEntityId?: string;
  allowNewEntry?: boolean;
  setIsNewConfig: (value: boolean) => void;
  selectedConfigName?: string;
}

export const PagesInventoryConfigUiConfigSelector = ({
  selectedConfigName: passedSelectedConfigName,
  setSelectedConfigName: passedSetSelectedConfigName,
  dealerEntityId: passedDealerEntityId,
  id,
  setIsNewConfig
}: PagesInventoryConfigUiResultsProps) => {
  const [selectedOption, setSelectedOption] = useState<ExtendedComboBoxOption>({
    label: passedSelectedConfigName || "",
    value: passedSelectedConfigName || ""
  });
  const [options, setOptions] = useState<ExtendedComboBoxOption[]>([
    {
      label: passedSelectedConfigName || "",
      value: passedSelectedConfigName || ""
    }
  ]);
  const dealerEntityId = useGetAccountId();

  if (!dealerEntityId) {
    throw new Error("No dealer entity id found");
  }
  const { data, error, isLoading } = useInventoryConfigsGet(
    passedDealerEntityId || dealerEntityId
  );
  useEffect(() => {
    if (data) {
      setOptions(generateOptions(data, passedSelectedConfigName));
    }
  }, [data, passedSelectedConfigName]);
  const value = useMemo(() => [selectedOption], [selectedOption]);
  if (error) {
    if ("status" in error) {
      const errMsg = error.message;

      return (
        <StyledAlert role="alert" sx={{ marginBottom: "16px" }} type="error">
          {errMsg}
        </StyledAlert>
      );
    } else {
      return (
        <StyledAlert role="alert" sx={{ marginBottom: "16px" }} type="error">
          {error.message}
        </StyledAlert>
      );
    }
  }

  if (isLoading) {
    return <LoadingIndicator actionName="Loading configs" />;
  }

  if (!data) {
    return (
      <StyledAlert role="alert" sx={{ marginBottom: "16px" }} type="info">
        No data
      </StyledAlert>
    );
  }

  const onChange = (e: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    const passedValue = e?.target.value;
    if (!passedValue) return;
    const selectedValue = passedValue[0]?.value;
    if (!selectedValue) return;
    const selectedValueLowercase = selectedValue.toLowerCase();
    // Compare lowercase versions to properly detect existing configs
    const foundConfig = options?.find(
      option => option.label?.toLowerCase() === selectedValueLowercase
    );
    setIsNewConfig(!foundConfig || foundConfig?.isNewConfig || false);
    if (!foundConfig) {
      setSelectedOption({
        label: selectedValueLowercase,
        value: selectedValueLowercase
      });
    } else {
      setSelectedOption({
        ...foundConfig,
        label: selectedValueLowercase,
        value: selectedValueLowercase
      });
    }
    passedSetSelectedConfigName(selectedValueLowercase);
  };

  return (
    <ComboBox
      allowNewEntry
      helpMessage="Listing IDs will always appear in lowercase"
      id={id}
      label="Inventory Listing ID"
      loadingMessage="Loading configs"
      multiple={false}
      options={options}
      sx={{ minWidth: "50%", maxWidth: "100%" }}
      value={value}
      onChange={onChange}
    />
  );
};
